import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  Popover,
  Stack,
  Text,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import { COLORS } from '../../utils/constants'

const footTrafficOptions = [
  { value: 'low', label: 'Low', range: '0–50' },
  { value: 'moderate', label: 'Moderate', range: '51–150' },
  { value: 'steady', label: 'Steady', range: '151–300' },
  { value: 'busy', label: 'Busy', range: '301–600' },
  { value: 'high', label: 'High', range: '601+' },
]

interface FootTrafficSelectorProps {
  selectedValues: string[]
  onChange: (values: string[]) => void
  opened: boolean
  onOpenedChange: (opened: boolean) => void
  summary: string
  isDefault: boolean
  onClear: () => void
}

const FootTrafficSelector = ({
  selectedValues,
  onChange,
  opened,
  onOpenedChange,
  summary,
  isDefault,
  onClear,
}: FootTrafficSelectorProps) => {
  const [localSelected, setLocalSelected] = useState<string[]>(selectedValues)

  // Sync local state with parent when popover opens
  useEffect(() => {
    if (opened) {
      setLocalSelected(selectedValues)
    }
  }, [opened, selectedValues])

  const getPillStyle = (active: boolean) => ({
    borderRadius: 30,
    marginRight: 8,
    color: active ? 'white' : COLORS.LightGreen,
    borderColor: COLORS.LightGreen,
    background: active ? COLORS.LightGreen : 'white',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    padding: '0 20px',
    minWidth: 120,
    cursor: 'pointer',
    border: `1px solid ${COLORS.LightGreen}`,
  })

  const handleOptionToggle = (value: string) => {
    setLocalSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    )
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    setLocalSelected([])
    onClear()
  }

  const handleDone = () => {
    // Only call onChange if values actually changed
    if (JSON.stringify(localSelected) !== JSON.stringify(selectedValues)) {
      onChange(localSelected)
    }
    onOpenedChange(false)
  }

  // When popover closes (not via Done), apply filter only if changed
  const handlePopoverChange = (popoverOpen: boolean) => {
    if (!popoverOpen) {
      // Only call onChange if values actually changed
      if (JSON.stringify(localSelected) !== JSON.stringify(selectedValues)) {
        onChange(localSelected)
      }
    }
    onOpenedChange(popoverOpen)
  }

  return (
    <Popover
      opened={opened}
      onChange={handlePopoverChange}
      position='bottom-start'
      shadow='md'
      width={250}
    >
      <Popover.Target>
        <Button
          variant='outline'
          radius='xl'
          style={getPillStyle(!isDefault)}
          onClick={() => onOpenedChange(!opened)}
        >
          <Group gap={8} align='center' wrap='nowrap'>
            {!isDefault && (
              <FontAwesomeIcon icon={faCheck} style={{ color: 'white' }} />
            )}
            <span>{summary}</span>
            {!isDefault ? (
              <ActionIcon
                size='xs'
                variant='subtle'
                color='white'
                onClick={handleClear}
              >
                <FontAwesomeIcon icon={faTimes} />
              </ActionIcon>
            ) : opened ? (
              <FontAwesomeIcon
                icon={faChevronUp}
                style={{
                  paddingLeft: 8,
                  fontSize: 16,
                }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{
                  paddingLeft: 8,
                  fontSize: 16,
                }}
              />
            )}
          </Group>
        </Button>
      </Popover.Target>

      <Popover.Dropdown>
        <Box p='md'>
          <Text size='sm' fw={500} mb={12}>
            Visitors Per Day
          </Text>
          <Stack gap={8}>
            {footTrafficOptions.map((option) => (
              <Checkbox
                key={option.value}
                label={`${option.label} (${option.range})`}
                checked={localSelected.includes(option.value)}
                onChange={() => handleOptionToggle(option.value)}
                styles={{
                  label: {
                    cursor: 'pointer',
                  },
                }}
              />
            ))}
          </Stack>
          <Button
            fullWidth
            mt={16}
            style={{ backgroundColor: COLORS.LightGreen, color: 'white' }}
            onClick={handleDone}
          >
            Done
          </Button>
        </Box>
      </Popover.Dropdown>
    </Popover>
  )
}

export default FootTrafficSelector
